/**
 * CoreStudioApi — the only data surface the back-office screens consume.
 *
 * The studio reads a pinned release snapshot, so reads are synchronous; authoring
 * mutations return the new record. Two implementations:
 *   • FixtureApi — seeded from the handoff corpus; fully interactive offline.
 *   • HttpApi    — stub wired to the Core serving/admin endpoints (read snapshot +
 *                  fire-and-forget mutations), so components can be pointed at a server
 *                  without changing a line.
 */
import type {
  AuditEntry,
  Client,
  ClassifyInput,
  ClassifyResult,
  CoverageEntry,
  Domain,
  GoldenCase,
  Jurisdiction,
  Meta,
  Person,
  ReleaseRecord,
  Role,
  RoleKey,
  Rule,
  RuleDiff,
  SourceOfLaw,
} from './types.js';
import { classifyAutomotive, classifyMedical } from './classify.js';
import { cellKey } from './coverage.js';
import * as seed from './fixtures.js';

export interface CoreStudioApi {
  // ── reference ──
  jurisdictions(): Jurisdiction[];
  domains(): Domain[];
  roles(): Record<RoleKey, Role>;
  people(): Record<RoleKey, Person>;
  clients(): Client[];
  meta(): Meta;
  releases(): ReleaseRecord[];

  // ── corpus ──
  rules(): Rule[];
  rule(id: string): Rule | undefined;
  rulesForCell(juris: string, domain: string): Rule[];

  // ── sources of law ──
  sources(): SourceOfLaw[];
  source(id: string): SourceOfLaw | undefined;

  // ── coverage ──
  coverage(): Record<string, CoverageEntry>;
  coverageCell(key: string): CoverageEntry | undefined;
  coverageList(): CoverageEntry[];

  // ── audit / diffs ──
  audit(): AuditEntry[];
  diff(id: string): RuleDiff | undefined;
  diffableRuleIds(): string[];

  // ── classification (no AI; rule only) ──
  classify(domain: 'medical' | 'automotive', input: ClassifyInput): ClassifyResult;
  goldenSet(domain: 'medical' | 'automotive'): GoldenCase[];

  // ── authoring (human-driven; reviewer approves) ──
  newRuleScaffold(juris: string, domain: string, author: string): Rule;
  createRule(juris: string, domain: string, author: string): Rule;
  updateRule(origId: string, draft: Rule): Rule;
  submitReview(origId: string, draft: Rule, actor: string): Rule;
  publishRule(origId: string, draft: Rule, wasPublished: boolean, actor: string): Rule;
  deleteRule(origId: string): void;
}

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function now(): string {
  const d = new Date();
  return today() + ' ' + d.toTimeString().slice(0, 5);
}

/**
 * FixtureApi — in-memory implementation over a deep clone of the seed corpus. Holds the
 * mutable authoring state so the SPA is fully interactive without a backend.
 */
export class FixtureApi implements CoreStudioApi {
  private _rules: Rule[];
  private _sources: SourceOfLaw[];
  private _coverage: Record<string, CoverageEntry>;
  private _audit: AuditEntry[];
  private _seq = 0;

  constructor() {
    this._rules = clone(seed.RULES);
    this._sources = clone(seed.SOURCES);
    this._coverage = clone(seed.COVERAGE);
    this._audit = clone(seed.AUDIT);
  }

  jurisdictions(): Jurisdiction[] {
    return seed.JURISDICTIONS;
  }
  domains(): Domain[] {
    return seed.DOMAINS;
  }
  roles(): Record<RoleKey, Role> {
    return seed.ROLES;
  }
  people(): Record<RoleKey, Person> {
    return seed.PEOPLE;
  }
  clients(): Client[] {
    return seed.CLIENTS;
  }
  meta(): Meta {
    return seed.META;
  }
  releases(): ReleaseRecord[] {
    return seed.RELEASES;
  }

  rules(): Rule[] {
    return this._rules;
  }
  rule(id: string): Rule | undefined {
    return this._rules.find((r) => r.ruleId === id);
  }
  rulesForCell(juris: string, domain: string): Rule[] {
    return this._rules.filter((r) => r.juris === juris && r.domain === domain);
  }

  sources(): SourceOfLaw[] {
    return this._sources;
  }
  source(id: string): SourceOfLaw | undefined {
    return this._sources.find((s) => s.id === id);
  }

  coverage(): Record<string, CoverageEntry> {
    return this._coverage;
  }
  coverageCell(key: string): CoverageEntry | undefined {
    return this._coverage[key];
  }
  coverageList(): CoverageEntry[] {
    return Object.values(this._coverage);
  }

  audit(): AuditEntry[] {
    return this._audit;
  }
  diff(id: string): RuleDiff | undefined {
    return seed.DIFFS[id];
  }
  diffableRuleIds(): string[] {
    return this._rules.filter((r) => seed.DIFFS[r.ruleId]).map((r) => r.ruleId);
  }

  classify(domain: 'medical' | 'automotive', input: ClassifyInput): ClassifyResult {
    return domain === 'medical'
      ? classifyMedical(input as Parameters<typeof classifyMedical>[0])
      : classifyAutomotive(input as Parameters<typeof classifyAutomotive>[0]);
  }
  goldenSet(domain: 'medical' | 'automotive'): GoldenCase[] {
    return domain === 'medical' ? seed.GOLDEN_MEDICAL : seed.GOLDEN_AUTO;
  }

  newRuleScaffold(juris: string, domain: string, author: string): Rule {
    const n = this._rules.length + 1;
    return {
      ruleId: `${juris || 'EU'}-NEW-Draft-R${n}`,
      juris: juris || 'EU',
      domain: domain || 'medical',
      topic: 'classification',
      outputType: 'classification',
      status: 'draft',
      version: 1,
      title: 'Untitled rule',
      condition: {
        kind: 'conditions',
        factors: [{ name: 'First factor', note: 'yes / no' }],
        clauses: [
          { when: 'Describe the facts that must be true', then: 'State the result (e.g. Class IIa)' },
          { when: 'Any other case', then: 'Abstain — no rule applies' },
        ],
      },
      output: { type: 'classification', value: '', detail: '' },
      citations: [],
      effectiveFrom: today(),
      effectiveTo: null,
      authoredBy: author,
      approvedBy: null,
      rationale: '',
      versions: [{ version: 1, date: today(), author, approver: null, note: 'Draft created.' }],
      tests: [],
      isNew: true,
    };
  }

  createRule(juris: string, domain: string, author: string): Rule {
    const r = this.newRuleScaffold(juris, domain, author);
    this._rules = [r, ...this._rules];
    this.pushAudit({
      actor: author,
      action: 'draft created',
      target: r.ruleId,
      from: null,
      to: 'v1',
      note: 'New draft started in the authoring workspace.',
      affected: 0,
      kind: 'draft',
    });
    return r;
  }

  updateRule(origId: string, draft: Rule): Rule {
    const d = clone(draft);
    delete d.isNew;
    this._rules = this._rules.map((r) => (r.ruleId === origId ? d : r));
    return d;
  }

  submitReview(origId: string, draft: Rule, actor: string): Rule {
    const d = clone(draft);
    delete d.isNew;
    d.status = 'in_review';
    this._rules = this._rules.map((r) => (r.ruleId === origId ? d : r));
    this.pushAudit({
      actor,
      action: 'sent for review',
      target: d.ruleId,
      from: null,
      to: 'v' + d.version,
      note: 'Submitted to a reviewer for legal sign-off.',
      affected: 0,
      kind: 'review',
    });
    return d;
  }

  publishRule(origId: string, draft: Rule, wasPublished: boolean, actor: string): Rule {
    const d = clone(draft);
    delete d.isNew;
    const prevVer = d.version;
    d.status = 'published';
    d.approvedBy = actor;
    if (!d.effectiveFrom) d.effectiveFrom = today();
    if (wasPublished) {
      d.version = prevVer + 1;
      d.versions = [
        { version: d.version, date: today(), author: d.authoredBy, approver: actor, note: 'Re-published from the authoring workspace.' },
        ...d.versions,
      ];
    } else {
      const rest = d.versions.filter((v) => v.version !== d.version);
      d.versions = [
        { version: d.version, date: today(), author: d.authoredBy, approver: actor, note: 'Approved and published.' },
        ...rest,
      ];
    }
    this._rules = this._rules.map((r) => (r.ruleId === origId ? d : r));
    if (!wasPublished) {
      const key = cellKey(d.juris, d.domain);
      const c = this._coverage[key];
      if (c) {
        const next: CoverageEntry = { ...c, rules: c.rules + 1 };
        if (next.state === 'empty') {
          next.state = 'thin';
          next.gate = 'abstain';
          next.topics = Math.max(1, next.topics);
        }
        this._coverage = { ...this._coverage, [key]: next };
      }
    }
    this.pushAudit({
      actor,
      action: 'rule published',
      target: d.ruleId,
      from: wasPublished ? 'v' + prevVer : null,
      to: 'v' + d.version,
      note: 'Approved and published from the authoring workspace.',
      affected: 0,
      kind: 'publish',
    });
    return d;
  }

  deleteRule(origId: string): void {
    this._rules = this._rules.filter((r) => r.ruleId !== origId);
  }

  private pushAudit(e: Omit<AuditEntry, 'id' | 'ts'>): void {
    this._seq += 1;
    this._audit = [{ id: `a-live-${this._seq}`, ts: now(), ...e }, ...this._audit];
  }
}

/**
 * HttpApi — stub client for the Core serving/admin endpoints (Core PRD §11, §14). Reads
 * are served from a snapshot hydrated at boot (the studio pins a release); mutations POST
 * to the admin API and optimistically return the new record. Endpoint paths are wired so
 * a real deployment only needs the network layer filled in; until then it composes the
 * FixtureApi snapshot so the interface is satisfied end-to-end.
 *
 * Components depend on CoreStudioApi only — swapping FixtureApi for HttpApi is a one-line
 * change at the composition root.
 */
export interface HttpApiOptions {
  baseUrl: string;
  /** Snapshot to serve reads from until a live hydrate lands. */
  snapshot?: FixtureApi;
}

export class HttpApi implements CoreStudioApi {
  readonly baseUrl: string;
  private readonly snap: FixtureApi;

  constructor(opts: HttpApiOptions) {
    this.baseUrl = opts.baseUrl.replace(/\/$/, '');
    this.snap = opts.snapshot ?? new FixtureApi();
  }

  /** GET helper against the Core serving API — kept for when the network layer is filled in. */
  private endpoint(path: string): string {
    return `${this.baseUrl}${path}`;
  }

  // reference + corpus reads come from the pinned snapshot
  jurisdictions(): Jurisdiction[] {
    return this.snap.jurisdictions();
  }
  domains(): Domain[] {
    return this.snap.domains();
  }
  roles(): Record<RoleKey, Role> {
    return this.snap.roles();
  }
  people(): Record<RoleKey, Person> {
    return this.snap.people();
  }
  clients(): Client[] {
    return this.snap.clients();
  }
  meta(): Meta {
    return this.snap.meta();
  }
  releases(): ReleaseRecord[] {
    return this.snap.releases();
  }
  rules(): Rule[] {
    return this.snap.rules();
  }
  rule(id: string): Rule | undefined {
    return this.snap.rule(id);
  }
  rulesForCell(juris: string, domain: string): Rule[] {
    return this.snap.rulesForCell(juris, domain);
  }
  sources(): SourceOfLaw[] {
    return this.snap.sources();
  }
  source(id: string): SourceOfLaw | undefined {
    return this.snap.source(id);
  }
  coverage(): Record<string, CoverageEntry> {
    return this.snap.coverage();
  }
  coverageCell(key: string): CoverageEntry | undefined {
    return this.snap.coverageCell(key);
  }
  coverageList(): CoverageEntry[] {
    return this.snap.coverageList();
  }
  audit(): AuditEntry[] {
    return this.snap.audit();
  }
  diff(id: string): RuleDiff | undefined {
    return this.snap.diff(id);
  }
  diffableRuleIds(): string[] {
    return this.snap.diffableRuleIds();
  }

  /** POST /v1/classify — the serving engine returns answer | abstain | no_coverage. */
  classify(domain: 'medical' | 'automotive', input: ClassifyInput): ClassifyResult {
    void this.endpoint('/v1/classify');
    return this.snap.classify(domain, input);
  }
  goldenSet(domain: 'medical' | 'automotive'): GoldenCase[] {
    return this.snap.goldenSet(domain);
  }

  newRuleScaffold(juris: string, domain: string, author: string): Rule {
    return this.snap.newRuleScaffold(juris, domain, author);
  }
  /** POST /v1/admin/rules */
  createRule(juris: string, domain: string, author: string): Rule {
    void this.endpoint('/v1/admin/rules');
    return this.snap.createRule(juris, domain, author);
  }
  /** PUT /v1/admin/rules/{id} */
  updateRule(origId: string, draft: Rule): Rule {
    void this.endpoint(`/v1/admin/rules/${origId}`);
    return this.snap.updateRule(origId, draft);
  }
  /** POST /v1/admin/rules/{id}/review */
  submitReview(origId: string, draft: Rule, actor: string): Rule {
    void this.endpoint(`/v1/admin/rules/${origId}/review`);
    return this.snap.submitReview(origId, draft, actor);
  }
  /** POST /v1/admin/rules/{id}/publish */
  publishRule(origId: string, draft: Rule, wasPublished: boolean, actor: string): Rule {
    void this.endpoint(`/v1/admin/rules/${origId}/publish`);
    return this.snap.publishRule(origId, draft, wasPublished, actor);
  }
  /** DELETE /v1/admin/rules/{id} */
  deleteRule(origId: string): void {
    void this.endpoint(`/v1/admin/rules/${origId}`);
    this.snap.deleteRule(origId);
  }
}
